// @flow
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { find, get, isEmpty, isNil } from 'lodash';
import { withTranslation } from 'react-i18next';
import { Button, Icon, Loading } from '@elements';
import type { PlanType, UserAvailablePlans } from '@types';
import { createNsTranslator, fsPrintf, useWindowSize, openSupportChat } from '@helpers/helpers';
import { SubscriptionHeader } from '@blocks';
import PlanCard from './PlanCard';
import PlanPicker from './PlanPicker';
import WebCheckoutPending from './WebCheckoutPending';
import { StoreReviewPlan } from '../StoreReview/StoreReviewPlan';
import './Pricing.scss';

// Below this width three cards don't fit side by side: phones get a plan picker instead.
const COMPACT_MAX_WIDTH = 720;

const PLAN_TYPES = ['basic', 'complete', 'concierge'];

type Props = {
  onSelectPlan: Function,
  onCheckSubscription: Function,
  availablePlans: UserAvailablePlans,
  t: Function,
  availablePlansLoading: boolean,
  // A checkout is being opened (for openingPlan): every subscribe button waits for it
  isOpeningCheckout: boolean,
  openingPlan: ?PlanType,
  webCheckout: ?{ plan: ?PlanType },
  isCheckingSubscription: boolean,
  subscriptionUnconfirmed: boolean,
  subscriptionStatus: ?string,
  trialEndsInDays: ?number,
  // An iOS build in App Store review: no plans, prices or purchase
  storeReview?: boolean,
  onLogout?: Function,
};

// The trial countdown from the old /assinar screen, now above the plans
const trialNotice = (status: ?string, days: ?number, _: Function) => {
  if (status === 'end_of_trial') {
    return { ended: true, text: _('label/end_of_trial_counter'), cta: _('label/end_of_trial_subscribe_now') };
  }
  if (status !== 'trialing') return null;

  const cta = _('label/trialing_subscribe_now');
  if (isNil(days)) return { ended: false, text: '', cta };

  const left = Math.max(0, Math.round(Number(days) || 0));
  let text = _('label/trialing_counter', null, fsPrintf(left));
  if (left === 0) text = _('label/trialing_counter_today');
  if (left === 1) text = _('label/trialing_counter_one');
  return { ended: false, text, cta };
};

const Pricing = ({
  availablePlans, availablePlansLoading, isOpeningCheckout, openingPlan, webCheckout,
  isCheckingSubscription, subscriptionUnconfirmed, subscriptionStatus, trialEndsInDays,
  onSelectPlan, onCheckSubscription, storeReview, onLogout, t,
}: Props) => {
  const _ = createNsTranslator('subscription', t);
  const { width } = useWindowSize();
  const compact = width < COMPACT_MAX_WIDTH;

  const plans = PLAN_TYPES
    .map((type) => ({ type, plan: find(availablePlans, (it) => String(it.plan).startsWith(type)) }))
    .filter(({ plan }) => !isEmpty(plan));

  const isOpening = (plan: ?PlanType) => isOpeningCheckout && openingPlan === plan;
  const notice = trialNotice(subscriptionStatus, trialEndsInDays, _);

  const handleShowIntercom = useCallback(() => {
    openSupportChat();
  }, []);

  if (storeReview) {
    return (
      <div id="pricingPage" className={classNames('appContent plansPage settingsPage', { compact })}>
        <SubscriptionHeader />
        <section className="mainSection">
          <StoreReviewPlan subscriptionStatus={subscriptionStatus} trialEndsInDays={trialEndsInDays} onLogout={onLogout} _={_} />
        </section>
      </div>
    );
  }

  return (
    <div id="pricingPage" className={classNames('appContent plansPage settingsPage', { compact })}>
      <SubscriptionHeader />
      <section className="mainSection">
        <div className="pricingContent">
          {!webCheckout && notice && (
            <p className={classNames('trialNotice', { ended: notice.ended })}>
              <Icon iconName="time" size={16} />
              <span>{notice.text && `${notice.text} `}<strong>{notice.cta}</strong></span>
            </p>
          )}

          {webCheckout && (
            <WebCheckoutPending
              onCheckSubscription={onCheckSubscription}
              onReopen={() => onSelectPlan(webCheckout.plan)}
              isOpeningCheckout={isOpening(webCheckout.plan)}
              disabled={isOpeningCheckout}
              isChecking={isCheckingSubscription}
              unconfirmed={subscriptionUnconfirmed}
              _={_}
            />
          )}

          <header className="pricingIntro">
            <h1>{_('label/pricing_title')}</h1>
            <p>{_('label/pricing_subtitle')}</p>
          </header>

          {availablePlansLoading && <Loading />}

          {!availablePlansLoading && compact && (
            <PlanPicker
              plans={plans}
              pendingPlan={get(webCheckout, 'plan')}
              onSubscribe={onSelectPlan}
              isOpening={isOpening}
              disabled={isOpeningCheckout}
              _={_}
            />
          )}

          {!availablePlansLoading && !compact && (
            <>
              <div className="planGrid">
                {plans.map(({ type, plan }) => (
                  <PlanCard
                    key={type}
                    type={type}
                    plan={plan}
                    onSubscribe={onSelectPlan}
                    isOpening={isOpening(plan.plan)}
                    disabled={isOpeningCheckout}
                    _={_}
                  />
                ))}
              </div>
              {/* Paying happens in the browser (no in-app purchase): say so before the user taps. */}
              <p className="webCheckoutNote">
                <Icon iconName="lock" size={14} />
                {_('label/web_checkout_note')}
              </p>
            </>
          )}

          {!availablePlansLoading && (
            <aside className="clinicCta">
              <Icon iconName="color-clinic" className="clinicIcon" />
              <div className="clinicCtaText">
                <h2>{_('label/quest_have_clinic')}</h2>
                <p>{_('label/clinic_cta_text')}</p>
              </div>
              <Button text={_('action/contact')} onClick={handleShowIntercom} />
            </aside>
          )}

          {!availablePlansLoading && window.isBR && <p className="smsNote">{_('label/sms_addon_note')}</p>}
        </div>
      </section>
    </div>
  );
};

export default withTranslation()(Pricing);
